import { useParams } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useSnapshot } from 'valtio';
import { useSiteDataLayerEnabled } from '~/hooks/useSiteDataLayerEnabled';
import fieldNotesStore from '~/pages/projectDetails/fieldNotes/store';
import {
  buildSiteDataLegendSection,
  type SiteDataLegendSection,
} from '~/pages/projectDetails/map/siteDataLegend.helpers';
import { filterGeoItemsByReportType, useListGeoItems } from '~/pages/projectDetails/potholing/common';
import rootStore from '~/store';
import layerStore from '~/store2/layers';
import { SITE_DATA_LAYER_IDS, shouldShowSiteDataInLegend } from '~/store2/siteDataLayers';

/**
 * The "Site Data" legend section for whichever project map is mounted, or `null` when no row
 * applies. Site Data renders as client GeoJSON with no `tilesConfig` entry, so the
 * queryRenderedFeatures → tilesConfig lookup both legends use can't see it and each needs this
 * bespoke section.
 *
 */
export function useSiteDataLegendSection(): SiteDataLegendSection | null {
  const { projectId, tabId } = useParams({ strict: false }) as { projectId?: string; tabId?: string };
  const { visibleLayerIds } = useSnapshot(layerStore) as typeof layerStore;
  const siteDataLayerEnabled = useSiteDataLayerEnabled();

  const potholesActive =
    !!projectId &&
    shouldShowSiteDataInLegend(visibleLayerIds, SITE_DATA_LAYER_IDS.potholes, tabId, siteDataLayerEnabled);
  const fieldNotesActive =
    !!projectId &&
    shouldShowSiteDataInLegend(visibleLayerIds, SITE_DATA_LAYER_IDS.fieldNotes, tabId, siteDataLayerEnabled);

  // Load-gated: a still-loading org leaves mobileReportType undefined, which must not be read as
  // potholeLog (that flashes a wrong/empty row for manhole orgs).
  const org = useSnapshot(rootStore).currentOrganization;
  const { data: geoItemsData } = useListGeoItems({ projectId: projectId ?? '', enabled: potholesActive });
  const { fieldNotes } = useSnapshot(fieldNotesStore) as typeof fieldNotesStore;

  return useMemo(
    () =>
      buildSiteDataLegendSection({
        potholesActive: potholesActive && !!org,
        potholeItems: potholesActive && org ? filterGeoItemsByReportType(geoItemsData ?? [], org.mobileReportType) : [],
        reportType: org?.mobileReportType,
        fieldNotesActive,
        fieldNotes,
      }),
    [potholesActive, org, geoItemsData, fieldNotesActive, fieldNotes],
  );
}
